import { useState, type ReactNode } from 'react';

import {
  DataTable,
  EmptyState,
  FileField,
  Form,
  FormError,
  Panel,
  SubmitButton,
  TextField,
  useNotifications,
  type Column,
} from '../../components';
import {
  BASELINE_COPY,
  COMPANY_BASELINE_COPY,
  SPREADSHEET_ACCEPT,
  isFormatRefusal,
  isMovedOnRefusal,
  movedOnContractsOf,
  rowProblemsOf,
  serverSentence,
  type MovedOnContract,
  type RowProblem,
} from './baselineImport';

/**
 * The customer's sheet, sent once and split across several contracts: the
 * form, and the four answers it can get, shared by the company screen
 * ([`CompanyBaselineImportPage`](./CompanyBaselineImportPage.tsx)) and the
 * group page ([`GroupBaselineImport`](./GroupBaselineImport.tsx)).
 *
 * What differs between the two is *where* the sheet goes and how a contract is
 * named -- by insurer and codes inside one company, by company as well inside a
 * group -- so those arrive as props. Everything else is one behaviour:
 *
 *  - **The override is a property of the last answer.** Changing the file or
 *    the date resets the request, and the checkbox goes with it; the button
 *    stays disabled until the box is ticked.
 *  - **Each refusal has its own panel.** Row problems as a table of lines, a
 *    file that is not a sheet as the server's sentence, and contracts that
 *    moved on as a table naming each one.
 *  - **Success is a table, not a toast.** The analyst checks the counts against
 *    the blocks of the sheet they just sent, so they stay on the page.
 */

/** What a sheet import screen sends, whatever it is sent to. */
export interface SheetImportRequest {
  readonly baselineDate: string;
  readonly file: File;
  readonly override?: boolean;
}

/** The slice of a mutation this component drives. */
export interface SheetImportMutation<TResult> {
  readonly error: unknown;
  readonly data: TResult | undefined;
  readonly isPending: boolean;
  reset(): void;
  run(request: SheetImportRequest, onSuccess: () => void): void;
}

/** The words that change between the company and the group screens. */
export interface SheetImportCopy {
  readonly formTitle: string;
  readonly formDescription: string;
  readonly fileHint: string;
  readonly noContracts: string;
  readonly noContractsDescription: string;
  readonly imported: string;
  readonly conflictDescription: string;
}

const PROBLEM_COLUMNS: readonly Column<RowProblem>[] = [
  { key: 'line', header: BASELINE_COPY.lineHeader, align: 'end', render: (row) => row.line },
  { key: 'column', header: BASELINE_COPY.columnHeader, render: (row) => row.column },
  { key: 'reason', header: BASELINE_COPY.reasonHeader, render: (row) => row.reason },
];

export function SheetBaselineImport<TResult>({
  copy,
  hasContracts,
  mutation,
  overrideId,
  movedOnColumns,
  renderResult,
}: {
  copy: SheetImportCopy;
  /** False when there is no contract to route a row to: the form is not offered. */
  hasContracts: boolean;
  mutation: SheetImportMutation<TResult>;
  /** The override checkbox's element id, unique on the page. */
  overrideId: string;
  /** How the moved-on table names each contract, and the server's sentence. */
  movedOnColumns: readonly Column<MovedOnContract>[];
  renderResult: (result: TResult) => ReactNode;
}) {
  const { notify } = useNotifications();

  const [file, setFile] = useState<File | null>(null);
  const [baselineDate, setBaselineDate] = useState('');
  const [override, setOverride] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  const problems = rowProblemsOf(mutation.error);
  const askedToOverride = isMovedOnRefusal(mutation.error);
  const movedOn = movedOnContractsOf(mutation.error);
  const rejectedWholeFile = isFormatRefusal(mutation.error);

  const startOver = () => {
    setOverride(false);
    setLocalError(null);
    mutation.reset();
  };

  const chooseFile = (chosen: File | null) => {
    setFile(chosen);
    startOver();
  };

  const chooseDate = (chosen: string) => {
    setBaselineDate(chosen);
    startOver();
  };

  const submit = () => {
    if (file === null) {
      setLocalError(BASELINE_COPY.missingFile);
      return;
    }
    if (baselineDate === '') {
      setLocalError(BASELINE_COPY.missingDate);
      return;
    }
    setLocalError(null);
    mutation.run({ baselineDate, file, ...(override && { override: true }) }, () => {
      setOverride(false);
      notify('success', copy.imported);
    });
  };

  return (
    <>
      <Panel title={copy.formTitle} description={copy.formDescription}>
        {!hasContracts ? (
          <EmptyState title={copy.noContracts} description={copy.noContractsDescription} />
        ) : (
          <Form onSubmit={submit} aria-label={copy.formTitle}>
            {!askedToOverride && !rejectedWholeFile && problems.length === 0 && (
              <FormError error={mutation.error} />
            )}
            {localError !== null && (
              <div className="form__error" role="alert">
                <p>{localError}</p>
              </div>
            )}
            <FileField
              label={COMPANY_BASELINE_COPY.fileLabel}
              hint={copy.fileHint}
              accept={SPREADSHEET_ACCEPT}
              required
              onChange={chooseFile}
            />
            <TextField
              label={BASELINE_COPY.baselineDateLabel}
              hint={BASELINE_COPY.baselineDateHint}
              type="date"
              value={baselineDate}
              onChange={chooseDate}
              required
            />
            {askedToOverride && (
              <div className="field">
                <label className="field__label" htmlFor={overrideId}>
                  <input
                    id={overrideId}
                    type="checkbox"
                    checked={override}
                    onChange={(event) => setOverride(event.target.checked)}
                  />{' '}
                  {COMPANY_BASELINE_COPY.overrideLabel}
                </label>
                {!override && <p className="field__hint">{BASELINE_COPY.overrideHint}</p>}
              </div>
            )}
            <div className="form__actions">
              <SubmitButton
                pending={mutation.isPending || (askedToOverride && !override)}
                pendingLabel={
                  mutation.isPending ? BASELINE_COPY.submitting : BASELINE_COPY.overrideSubmit
                }
              >
                {askedToOverride ? BASELINE_COPY.overrideSubmit : COMPANY_BASELINE_COPY.submit}
              </SubmitButton>
            </div>
          </Form>
        )}
      </Panel>

      {askedToOverride && (
        <Panel title={COMPANY_BASELINE_COPY.conflictTitle} description={copy.conflictDescription}>
          {movedOn.length > 0 ? (
            <DataTable
              caption={COMPANY_BASELINE_COPY.conflictCaption}
              columnVisibility={false}
              columns={movedOnColumns}
              rows={movedOn}
              rowKey={(row) => row.contractId}
            />
          ) : (
            <p className="panel__footnote">
              {BASELINE_COPY.serverSaid} <q>{serverSentence(mutation.error)}</q>
            </p>
          )}
        </Panel>
      )}

      {rejectedWholeFile && (
        <Panel title={BASELINE_COPY.formatTitle} description={BASELINE_COPY.formatDescription}>
          <p className="panel__footnote">
            {BASELINE_COPY.serverSaid} <q>{serverSentence(mutation.error)}</q>
          </p>
        </Panel>
      )}

      {problems.length > 0 && (
        <Panel title={BASELINE_COPY.rejectedTitle} description={BASELINE_COPY.rejectedDescription}>
          <DataTable
            caption={BASELINE_COPY.rejectedCaption}
            columnVisibility={false}
            columns={PROBLEM_COLUMNS}
            rows={problems}
            rowKey={(row, index) => `${row.line}-${index}`}
          />
        </Panel>
      )}

      {mutation.data !== undefined && renderResult(mutation.data)}
    </>
  );
}
